import {Component, Input, OnInit} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  ConfigurationState,
  InspectionImageDto,
  LocationOption,
  PostingDataResponseModel,
  EditPostingState,
  EditPostingFormNames
} from 'remarketing-angular-library';
import { cloneDeep, get, isNil, set } from 'lodash';
import { CustomValidatorConfig, PhotoUploadService } from '../../services/photo-upload.service';
import { takeUntil } from 'rxjs/operators';
import { TireFormData } from '../../services/tires-form.service';
import { PostingFormDirective } from '../posting-form/posting-form.directive';
import { ViewportScroller } from '@angular/common';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { TranslocoDirective } from '@jsverse/transloco';
import { PhotosCardComponent } from '../photos-card/photos-card.component';

@Component({
    selector: 'app-photos-form',
    templateUrl: './photos-form.component.html',
    styleUrls: ['./photos-form.component.scss'],
    imports: [TranslocoDirective, PhotosCardComponent]
})
export class PhotosFormComponent extends PostingFormDirective implements OnInit {
  postingData: PostingDataResponseModel;
  uploadedImgList: InspectionImageDto[];
  locationOptList: LocationOption[];
  inspectionTiresList: TireFormData[];
  doesImgListHaveFC = false;
  vehicleVINNumber: string;
  errMsg: string;
  photoFormCustomValidator: CustomValidatorConfig = {
    validatorName: 'validImageCount',
    validatorValue: 50,
    validatorErrMsgName: 'PHOTO_COUNT_ERROR',
    validatorErrMsgValue: "You can't upload more than fifty images."
  };
  @Input() postingTypeString: string;

  constructor(
    private readonly store: Store,
    public readonly photoUploadService: PhotoUploadService,
    public readonly editPostingUtilsService: EditPostingUtilsService,
    public readonly scroller: ViewportScroller,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService
  ) {
    super();
  }

  ngOnInit() {
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = cloneDeep(postingData);
      });
    this.inspectionTiresList = get(this.postingData, 'inventoryItem.inspectionReport.inspectionData.vehicle.tires', []);
    this.vehicleVINNumber = this.postingData.inventoryItem.vehicleInformation.vin;
    this.constructPhotoList();
    this.locationOptList = this.store.selectSnapshot(EditPostingState.locationOptions);
    this.getUpdatesFromPhotoService();
    this.sendPageloadAnalyticsEvent();
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.IMAGES_PHOTOS
    );
  }

  //constructing tires and existing inspection images
  constructPhotoList() {
    this.uploadedImgList = this.editPostingUtilsService.constructExistingPhotosList();

    this.setDefaultLocationValueIfNoLocationExistsOnImage(this.uploadedImgList);

    if (this.uploadedImgList && this.uploadedImgList.length) {
      this.orderMainImg();
    }
  }

  /**
   * Sets default image location for image objects with no location property value.
   * This scenario can occur with third party posting image data.
   */
  setDefaultLocationValueIfNoLocationExistsOnImage(imageList: InspectionImageDto[]): void {
    const unAssignedLocationValue = '';
    imageList.forEach(image => {
      if (isNil(image.location)) {
        image.location = unAssignedLocationValue;
      }
    });
  }

  /**
   *
   * @param selectedOption - selected location option index and option value
   * saves the location option in the store
   */
  public saveSelectedLocOpt(selectedOption) {
    const { locListIndex, selectedOpt } = selectedOption;
    set(this.uploadedImgList, `[${locListIndex}].location`, selectedOpt);
    if (!this.doesImgListHaveFC || (locListIndex === 0 && this.doesImgListHaveFC)) {
      this.orderMainImg();
    }
    this.editPostingUtilsService.updateInspectionImageData(this.uploadedImgList, true);
  }

  //order FC location card as first item of the list
  orderMainImg() {
    const mainImgIndex = this.uploadedImgList.findIndex(item => {
      return item.location === 'FC';
    });
    if (mainImgIndex >= 0) {
      const currentImgList = cloneDeep(this.uploadedImgList);
      const removedImg = currentImgList.splice(mainImgIndex, 1);
      currentImgList.unshift(removedImg[0]);
      this.uploadedImgList = currentImgList;
      this.doesImgListHaveFC = true;
    } else {
      this.doesImgListHaveFC = false;
    }
  }

  /**
   *
   * @param actionName - incoming action from action-bar comp
   * process the image list according to incoming action
   */
  actionBarUpdatesFromPhotosCard(data?) {
    const actionName = data.actionName;
    if (actionName === 'delete') {
      this.deleteSelectedImage();
    } else {
      this.uploadedImgList.forEach((item: any) => {
        delete item.isSelected;
      });
    }
    this.editPostingUtilsService.updateInspectionImageData(this.uploadedImgList, true);
  }

  //delete the selected image and closes the bar and modals
  deleteSelectedImage() {
    this.uploadedImgList = this.uploadedImgList.filter((item: any) => {
      return !item.isSelected && !item.showSpinner;
    });
    this.orderMainImg();
  }

  //Subscribe subjects from photo upload service
  getUpdatesFromPhotoService() {
    //gets VIW response from photo upload service
    this.photoUploadService.photoUploadResponseData.pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
      const response = data && data.response;
      if (response && response.showSpinner) {
        const imgCountBeforeMerge = this.uploadedImgList.length - 1 > 0 ? this.uploadedImgList.length - 1 : 1;
        this.uploadedImgList.push(...response.spinnerList);
        //scroll the screen to newly added spinner card
        this.scroller.scrollToAnchor(`image-photo-${imgCountBeforeMerge}`);
      } else {
        this.updateImageList(response);
      }
    });

    //gets error status from photo upload service
    this.photoUploadService.photoUploadErrStatus.pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
      this.errMsg = data && data.errMsg;
      if (this.errMsg) {
        this.uploadedImgList = this.uploadedImgList.filter((item: any) => {
          return !item.showSpinner;
        });
      }
    });
  }

  //updates the uploaded image url in the existing image list
  updateImageList(imageDetails) {
    const findSpinnerIndex = this.uploadedImgList.findIndex((imgItem: any) => {
      return imgItem.showSpinner;
    });
    const imgObj = {
      sourceUrl: imageDetails.files[0].fullSizedUrl,
      location: ''
    };
    this.uploadedImgList.splice(findSpinnerIndex, 1, imgObj);
    this.editPostingUtilsService.updateInspectionImageData(this.uploadedImgList, true);
  }
}
